import React from 'react';
import { Box, Stack, Typography, Avatar, Paper } from '@mui/material';
import { FileText, User, Clock } from 'lucide-react';

export interface ITranscript {
  speaker: string;
  text: string;
}

export interface ITranscripts {
  data: ITranscript[];
}

export const Transcripts: React.FC<ITranscripts> = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Stack
        alignItems='center'
        justifyContent='center'
        sx={{ py: 8, px: 2 }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'linear-gradient(to bottom right, #f3f4f6, #e5e7eb)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <FileText size={40} style={{ color: '#9ca3af' }} />
        </Box>
        <Typography variant='h6' sx={{ color: 'text.secondary', mb: 1, fontWeight: 600 }}>
          No Transcript Available
        </Typography>
        <Typography variant='body2' sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 380 }}>
          The interview transcript will appear here once the recording is
          processed.
        </Typography>
      </Stack>
    );
  }

  const getInitials = (name: string) => {
    const words = name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <Stack spacing={3} sx={{ p: 2, width: '100%' }}>
      {data.map((entry, index) => {
        const isPanelist =
          entry.speaker.toLowerCase().includes('panelist') ||
          entry.speaker.toLowerCase().includes('penalist');

        const initials = getInitials(entry.speaker);

        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              width: '100%',
              gap: 1.5,
              justifyContent: isPanelist ? 'flex-start' : 'flex-end',
            }}
          >
            {/* Avatar for panelist (left side) */}
            {isPanelist && (
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  background: 'linear-gradient(to bottom right, #3b82f6, #4f46e5)',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  boxShadow: 2,
                  flexShrink: 0,
                }}
              >
                {initials}
              </Avatar>
            )}

            {/* Message bubble */}
            <Box
              sx={{
                maxWidth: '75%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: isPanelist ? 'flex-start' : 'flex-end',
              }}
            >
              {/* Speaker name */}
              <Box
                sx={{
                  mb: 0.5,
                  px: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <User size={12} style={{ color: isPanelist ? '#3b82f6' : '#22c55e' }} />
                <Typography
                  variant='caption'
                  sx={{ fontWeight: 600, color: 'text.secondary' }}
                >
                  {entry.speaker}
                </Typography>
              </Box>

              {/* Message content */}
              <Paper
                elevation={isPanelist ? 0 : 2}
                sx={{
                  position: 'relative',
                  p: 2,
                  borderRadius: 2,
                  borderTopLeftRadius: isPanelist ? 0 : undefined,
                  borderTopRightRadius: !isPanelist ? 0 : undefined,
                  bgcolor: isPanelist ? 'action.hover' : 'primary.main',
                  color: isPanelist ? 'text.primary' : 'primary.contrastText',
                  border: isPanelist ? '1px solid' : 'none',
                  borderColor: isPanelist ? 'divider' : 'none',
                }}
              >
                <Typography variant='body2' sx={{ lineHeight: 1.6, color: 'inherit' }}>
                  {entry.text}
                </Typography>

                <Box
                  sx={{
                    mt: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    opacity: 0.75,
                  }}
                >
                  <Clock size={12} />
                  <Typography variant='caption' sx={{ fontSize: '0.6875rem' }}>
                    Segment {index + 1}
                  </Typography>
                </Box>
              </Paper>
            </Box>

            {/* Avatar for candidate (right side) */}
            {!isPanelist && (
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  background: 'linear-gradient(to bottom right, #22c55e, #059669)',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  boxShadow: 2,
                  flexShrink: 0,
                }}
              >
                {initials}
              </Avatar>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

export default Transcripts;
